/**
 * Render System - Three.js rendering pipeline management
 */

import * as THREE from 'three';

export class RenderSystem {
    constructor(canvas) {
        this.canvas = canvas;
        this.renderer = null;
        this.scene = null;
        this.camera = null;

        // Render settings
        this.settings = {
            antialias: true,
            alpha: false,
            powerPreference: 'high-performance',
            stencil: false,
            depth: true
        };

        // Performance monitoring
        this.frameCount = 0;
        this.lastFPSUpdate = 0;
        this.currentFPS = 0;

        // Post-processing (future use)
        this.composer = null;
        this.isPostProcessingEnabled = false;

        // Dynamic quality control
        this.autoQuality = true;
        this.minPixelRatio = 0.6;
        this.maxPixelRatio = Math.min(window.devicePixelRatio || 1, 2);
        this.lastQualityAdjust = 0;
        this.qualityAdjustCooldownMs = 2000;
    }

    init() {

        // Create WebGL renderer
        this.renderer = new THREE.WebGLRenderer({
            canvas: this.canvas,
            antialias: this.settings.antialias,
            alpha: this.settings.alpha,
            powerPreference: this.settings.powerPreference,
            stencil: this.settings.stencil,
            depth: this.settings.depth
        });

        // Configure renderer
        this.setupRenderer();

        // Set initial size
        this.handleResize();

    }

    setupRenderer() {
        // Set pixel ratio for sharp rendering on high-DPI displays (性能优化：降低初始值)
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.2)); // 从1.5降到1.2

        // Enable shadows
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

        // Set background color (overridden by skybox). Darker to match ocean.
        this.renderer.setClearColor(0x0b3353, 1);

        // Configure tone mapping for better lighting
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 0.85; // 降低整体曝光，减少“泛白/雾感”

        // Enable physically correct lights
        this.renderer.physicallyCorrectLights = true;

        // Output encoding for better color reproduction
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;

        // Configure gamma correction
        this.renderer.gammaFactor = 2.2;
    }

    setScene(scene) {
        this.scene = scene;
    }

    setCamera(camera) {
        this.camera = camera;
    }

    render() {
        if (!this.renderer || !this.scene || !this.camera) return;

        // Update performance monitoring
        this.updatePerformanceStats();

        // Render the scene
        this.renderer.render(this.scene, this.camera);
    }

    updatePerformanceStats() {
        this.frameCount++;
        const now = performance.now();

        // Update FPS every second
        if (now - this.lastFPSUpdate >= 1000) {
            this.currentFPS = Math.round((this.frameCount * 1000) / (now - this.lastFPSUpdate));
            this.frameCount = 0;
            this.lastFPSUpdate = now;

            // Optional: Log FPS for debugging
            // console.log(`FPS: ${this.currentFPS}`);

            // Auto quality scaling
            if (this.autoQuality && this.renderer) {
                const sinceAdjust = now - (this.lastQualityAdjust || 0);
                if (sinceAdjust > this.qualityAdjustCooldownMs) {
                    const currentPR = this.renderer.getPixelRatio();
                    let targetPR = currentPR;
                    if (this.currentFPS < 48 && currentPR > this.minPixelRatio) {
                        targetPR = Math.max(this.minPixelRatio, currentPR - 0.25);
                    } else if (this.currentFPS > 58 && currentPR < this.maxPixelRatio) {
                        targetPR = Math.min(this.maxPixelRatio, currentPR + 0.25);
                    }
                    if (targetPR !== currentPR) {
                        this.renderer.setPixelRatio(targetPR);
                        this.lastQualityAdjust = now;
                    }

                    // Adjust shadow quality dynamically
                    if (this.currentFPS < 50) {
                        if (this.renderer.shadowMap.enabled && this.renderer.shadowMap.type !== THREE.BasicShadowMap) {
                            this.renderer.shadowMap.type = THREE.BasicShadowMap;
                        }
                    } else if (this.currentFPS > 58) {
                        if (this.renderer.shadowMap.enabled && this.renderer.shadowMap.type !== THREE.PCFSoftShadowMap) {
                            this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
                        }
                    }
                }
            }
        }
    }

    handleResize() {
        if (!this.renderer) return;

        const width = window.innerWidth;
        const height = window.innerHeight;

        // Update renderer size
        this.renderer.setSize(width, height);

        // Update camera aspect ratio if available
        if (this.camera && this.camera.isPerspectiveCamera) {
            this.camera.aspect = width / height;
            this.camera.updateProjectionMatrix();
        }

    }

    // Get current rendering information
    getRenderInfo() {
        if (!this.renderer) return null;

        return {
            fps: this.currentFPS,
            triangles: this.renderer.info.render.triangles,
            geometries: this.renderer.info.memory.geometries,
            textures: this.renderer.info.memory.textures,
            calls: this.renderer.info.render.calls,
            lines: this.renderer.info.render.lines,
            points: this.renderer.info.render.points
        };
    }

    // Screenshot functionality
    takeScreenshot(width = 1920, height = 1080) {
        if (!this.renderer || !this.scene || !this.camera) return null;

        // Store current size
        const currentSize = this.renderer.getSize(new THREE.Vector2());

        // Temporarily resize for screenshot
        this.renderer.setSize(width, height);

        // Update camera aspect ratio
        if (this.camera.isPerspectiveCamera) {
            this.camera.aspect = width / height;
            this.camera.updateProjectionMatrix();
        }

        // Render frame
        this.renderer.render(this.scene, this.camera);

        // Get image data
        const dataURL = this.renderer.domElement.toDataURL('image/png');

        // Restore original size
        this.renderer.setSize(currentSize.x, currentSize.y);

        // Restore camera aspect ratio
        if (this.camera.isPerspectiveCamera) {
            this.camera.aspect = currentSize.x / currentSize.y;
            this.camera.updateProjectionMatrix();
        }

        return dataURL;
    }

    // Quality settings management
    setQuality(level) {
        if (!this.renderer) return;

        switch (level) {
            case 'low':
                this.renderer.setPixelRatio(0.5);
                this.renderer.shadowMap.enabled = false;
                break;

            case 'medium':
                this.renderer.setPixelRatio(1);
                this.renderer.shadowMap.enabled = true;
                this.renderer.shadowMap.type = THREE.BasicShadowMap;
                break;

            case 'high':
                this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
                this.renderer.shadowMap.enabled = true;
                this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
                break;

            case 'ultra':
                this.renderer.setPixelRatio(window.devicePixelRatio);
                this.renderer.shadowMap.enabled = true;
                this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
                break;
        }

    }

    // Enable wireframe mode for debugging
    enableWireframe(enable = true) {
        if (!this.scene) return;

        this.scene.traverse((object) => {
            if (object.isMesh && object.material) {
                if (Array.isArray(object.material)) {
                    object.material.forEach(material => {
                        material.wireframe = enable;
                    });
                } else {
                    object.material.wireframe = enable;
                }
            }
        });

    }

    // Get WebGL context information
    getWebGLInfo() {
        if (!this.renderer) return null;

        const gl = this.renderer.getContext();
        return {
            vendor: gl.getParameter(gl.VENDOR),
            renderer: gl.getParameter(gl.RENDERER),
            version: gl.getParameter(gl.VERSION),
            shadingLanguageVersion: gl.getParameter(gl.SHADING_LANGUAGE_VERSION),
            maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
            maxViewportDims: gl.getParameter(gl.MAX_VIEWPORT_DIMS),
            maxRenderBufferSize: gl.getParameter(gl.MAX_RENDERBUFFER_SIZE)
        };
    }

    // Dispose of renderer and free memory
    dispose() {
        if (this.renderer) {
            this.renderer.dispose();
            this.renderer = null;
        }

        if (this.composer) {
            this.composer.dispose();
            this.composer = null;
        }

        this.scene = null;
        this.camera = null;

    }
}
